import { readCookie, sharedCookieDomain, writeCookie } from '@ui/lib/cookies'

// The one opt-out bucket: sale/share, i.e. advertising. Analytics always runs.
export interface ConsentState {
  advertising: boolean
}

// `gpc`: the signal was present when the choice was saved.
export interface ConsentRecord extends ConsentState {
  gpc: boolean
}

declare global {
  interface Navigator {
    // Global Privacy Control (https://w3c.github.io/gpc/)
    globalPrivacyControl?: boolean
  }
}

export const CONSENT_COOKIE_NAME = 'atm_consent'

// CPRA: an opt-out must be honored for 12 months before asking again.
export const CONSENT_MAX_AGE_SEC = 60 * 60 * 24 * 365

export const ACCEPT_ALL: ConsentState = { advertising: true }
export const REJECT_ALL: ConsentState = { advertising: false }

// Opt-out model: advertising runs until the visitor says otherwise.
export const CONSENT_DEFAULTS: ConsentState = ACCEPT_ALL

// Anything but a boolean `advertising` is no decision; a missing `gpc` means
// saved without the signal.
export function parseConsentRecord(
  decoded: string | undefined,
): ConsentRecord | undefined {
  if (!decoded) return undefined
  try {
    const parsed = JSON.parse(decoded)
    if (typeof parsed?.advertising !== 'boolean') return undefined
    return { advertising: parsed.advertising, gpc: parsed.gpc === true }
  } catch {
    return undefined
  }
}

export function readConsentRecord(): ConsentRecord | undefined {
  return parseConsentRecord(readCookie(CONSENT_COOKIE_NAME))
}

export function writeConsentRecord({ advertising, gpc }: ConsentRecord) {
  if (typeof window === 'undefined') return
  writeCookie(CONSENT_COOKIE_NAME, JSON.stringify({ advertising, gpc }), {
    maxAgeSec: CONSENT_MAX_AGE_SEC,
    domain: sharedCookieDomain(window.location.hostname),
  })
}

export const hasGpcSignal = () =>
  typeof navigator !== 'undefined' && Boolean(navigator.globalPrivacyControl)

// GPC = "do not sell or share": advertising off. Only a choice saved under the
// signal overrides it. bootstrap.ts mirrors this.
export function resolveEffectiveConsent({
  record,
  gpc,
}: {
  record: ConsentRecord | undefined
  gpc: boolean
}): ConsentState {
  if (record) {
    return { advertising: record.advertising && !(gpc && !record.gpc) }
  }
  if (gpc) return { advertising: false }
  return { ...CONSENT_DEFAULTS }
}

export function readEffectiveConsent(): ConsentState {
  return resolveEffectiveConsent({
    record: readConsentRecord(),
    gpc: hasGpcSignal(),
  })
}
